import React from 'react';

import { RowDataUpdatedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageError, messageInfo, messageWarning, messageBoxText } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { company, CompanyDO } from '../../requests';

import Edit from './Edit';

type Props = unknown;

interface State {
  rowId?: number;
  update?: any;
  loading: boolean;
  data?: CompanyDO[];
  showGrid?: boolean;
  showValueSelector?: boolean;
}

export default class List extends React.Component <Props, State> {
  private pid: string;
  private mounted: boolean;
  private buttons: TableButtonsConfig;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.buttons = {
      add: hasLevel('role', ['ADM', 'URW']),
      del: hasLevel('role', ['ADM', 'URW']),
      edit: true,
    };

    this.state = {
      update: window.getStr(),
      loading: false,
      data: [],

      showGrid: true,
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const companyArray: CompanyDO[] = await company<CompanyDO[]>('list') || [];
      this.setLocalState({ data: companyArray });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setLocalState({
      update: window.getStr(),
      loading: false,
    });
  };

  public updModal = (modalName: string = 'none', sh: boolean = false): void => {
    this.setLocalState({
      update: window.getStr(),
      [modalName]: sh,
    });
  };

  public loadTab = (rowId: number): void => {
    const tabId: string = 'CoE';
    const title: string = rowId ? `Company Edit: ${rowId}` : 'Company New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: Edit,
      props: {
        rowId,
        tabId: this.pid,
        report: 'COMPANIES',
        title: 'Companies',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load().catch(() => { /* pass */ });
  };

  public onEdit = (item: CompanyDO): void => {
    window.store.dispatch(removeTab('CoE'));
    const rowId: number = item.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (item: CompanyDO): void => {
    const confirm: string = String(item.id);
    messageBoxText('Delete', `Enter '${confirm}' to confirm you want to delete the item: ${item.name}?`, (value?: string) => {
      if (confirm === value) {
        this.loadingStart();
        company('del', String(item.id))
          ?.then(() => {
            messageInfo('Success', 'Register successfully deleted', 5000);
            this.load().catch(() => { /* pass */ });
          })
          .catch((error: any) => {
            messageError('Error', getErrorMessage(error));
          })
          .finally(() => {
            this.loadingFinish();
          });
      } else {
        messageWarning('Warning', 'Wrong input parameter', 5000);
      }
    }, 'remove-circle', undefined, confirm);
  };

  public rowDataUpdated = (_event: RowDataUpdatedEvent): void => {
    this.setLocalState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: any = {
      id: 'ID',
      code: 'Code',
      name: 'Name',
      type: 'Type',
      contact: 'Contact',
      phone: 'Phone',
      email: 'E-mail'
    };
    const columnsToHide: any = [];
    const columnStyles: any = {
      name: { textAlign: 'left' },
      type: { textAlign: 'left' },
      contact: { textAlign: 'left' },
      phone: { textAlign: 'left' },
      email: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          hiddenCols={columnsToHide}
          customCellStyles={columnStyles}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          loading={loading}
          rowDataUpdated={this.rowDataUpdated}
        />
      </div>
    );
  }
}
